<template>
  <div class="cart-page">
    <!-- En-tête -->
    <div class="cart-header">
      <h1 class="cart-title">
        <span class="title-icon">🛒</span>
        Mon Panier
        <span class="cart-count" v-if="cart.length > 0">{{ cart.length }} article(s)</span>
      </h1>
      <p class="cart-subtitle" v-if="cart.length > 0">Vérifiez vos articles et procédez au paiement</p>
    </div>

    <!-- Panier vide -->
    <div v-if="cart.length === 0" class="empty-cart">
      <div class="empty-cart-icon">🛍️</div>
      <h2>Votre panier est vide</h2>
      <p>Découvrez notre sélection de produits et trouvez ce qui vous convient.</p>
      <button @click="continueShopping" class="btn btn-primary btn-lg">
        Découvrir les produits
      </button>
    </div>

    <!-- Panier avec articles -->
    <div v-else class="cart-container">
      <!-- Liste des articles -->
      <div class="cart-items">
        <div v-for="(item, index) in cart" :key="index" class="cart-item">
          <div class="item-image-wrapper">
            <img 
              :src="getProductImage(item.image)" 
              :alt="item.name" 
              class="item-image" 
              @error="handleImageError"
            />
            <span class="item-badge">{{ item.quantity }}</span>
          </div>
          
          <div class="item-details">
            <h3 class="item-name">{{ item.name }}</h3>
            <p class="item-price">{{ item.price }} <span class="currency">MAD</span></p>
            
            <div class="item-actions">
              <div class="quantity-controls">
                <button @click="decreaseQuantity(item)" class="qty-btn" :disabled="item.quantity <= 1">
                  <span class="qty-icon">−</span>
                </button>
                <span class="item-quantity">{{ item.quantity }}</span>
                <button @click="increaseQuantity(item)" class="qty-btn">
                  <span class="qty-icon">+</span>
                </button>
              </div>
              
              <button @click="removeFromCart(item)" class="btn-remove">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </button>
            </div>
          </div>
          
          <div class="item-total">
            <span class="total-label">Total</span>
            <span class="total-amount">{{ (item.price * item.quantity).toFixed(2) }} MAD</span>
          </div>
        </div>
      </div>

      <!-- Résumé de la commande -->
      <div class="cart-summary">
        <h3 class="summary-title">Résumé de la commande</h3>
        
        <div class="summary-row">
          <span>Sous-total</span>
          <span>{{ totalPrice.toFixed(2) }} MAD</span>
        </div>
    
        <div class="summary-divider"></div>
        <div class="summary-row total">
          <span>Total TTC</span>
          <span>{{ totalPrice.toFixed(2) }} MAD</span>
        </div>
        
        <div class="summary-actions">
          <button @click="continueShopping" class="btn btn-secondary">
            Continuer mes achats
          </button>
          <button @click="checkout" class="btn btn-primary btn-checkout">
            <span>Commander</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"></path>
              <path d="M12 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
        
        <!-- Paiement sécurisé avec logos -->
        <div class="secure-payment">
          <div class="secure-icons">
            <span>🔒</span>
            <span>💳</span>
            <span>🏦</span>
          </div>
          <p>Paiement 100% sécurisé</p>
          <div class="payment-methods">
            <!-- Logo Visa -->
            <div class="payment-logo visa" title="Visa">
              <svg viewBox="0 0 48 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.6 0.8L14.4 15.2H11.2L14.4 0.8H17.6Z" fill="#1434CB"/>
                <path d="M9.6 0.8L6.72 10.56L6.4 8.96C5.92 7.36 4.48 5.76 3.04 5.12L5.76 15.2H8.96L12.8 0.8H9.6Z" fill="#1434CB"/>
                <path d="M3.2 0.8H0L0.16 1.28C2.4 1.92 4.16 3.2 5.12 4.8L4.16 0.8H3.2Z" fill="#1434CB"/>
                <path d="M38.4 0.8L35.2 15.2H32L35.2 0.8H38.4Z" fill="#1434CB"/>
                <path d="M30.4 0.8L27.52 10.56L27.2 8.96C26.72 7.36 25.28 5.76 23.84 5.12L26.56 15.2H29.76L33.6 0.8H30.4Z" fill="#1434CB"/>
                <path d="M24 0.8H20.8L20.96 1.28C23.2 1.92 24.96 3.2 25.92 4.8L24.96 0.8H24Z" fill="#1434CB"/>
                <rect x="1.6" y="12.8" width="44.8" height="3.2" rx="1.6" fill="#F5F5F5"/>
                <circle cx="6.4" cy="14.4" r="1.6" fill="#1434CB"/>
              </svg>
            </div>

            <!-- Logo Mastercard -->
            <div class="payment-logo mastercard" title="Mastercard">
              <svg viewBox="0 0 48 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="8" r="7" fill="#EB001B"/>
                <circle cx="32" cy="8" r="7" fill="#F79E1B"/>
                <path d="M24 11.5C25.8 9.8 26.5 7.8 26.5 5.5C26.5 3.2 25.8 1.2 24 -0.5C22.2 1.2 21.5 3.2 21.5 5.5C21.5 7.8 22.2 9.8 24 11.5Z" fill="#FF5F00"/>
                <rect x="0" y="13" width="48" height="3" rx="1.5" fill="#F5F5F5" opacity="0.5"/>
              </svg>
            </div>

            <!-- Logo PayPal -->
            <div class="payment-logo paypal" title="PayPal">
              <svg viewBox="0 0 48 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.8 0.8H13.2L11.2 12.8H14.8L16.8 0.8Z" fill="#003087"/>
                <path d="M21.6 0.8H18L16 12.8H19.6L21.6 0.8Z" fill="#003087"/>
                <path d="M26.4 0.8H22.8L20.8 12.8H24.4L26.4 0.8Z" fill="#003087"/>
                <path d="M31.2 0.8H27.6L25.6 12.8H29.2L31.2 0.8Z" fill="#003087"/>
                <rect x="0" y="13" width="48" height="3" rx="1.5" fill="#F5F5F5" opacity="0.5"/>
              </svg>
            </div>

            <!-- Logo Stripe (optionnel) -->
            <div class="payment-logo stripe" title="Stripe">
              <svg viewBox="0 0 48 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 4.8C8 3.6 8.8 2.8 10.4 2.8C11.6 2.8 12.8 3.2 13.6 3.6L14.8 1.2C13.6 0.4 12 0 10.4 0C7.2 0 4.8 1.6 4.8 4.4C4.8 8.4 10.4 7.2 10.4 8.8C10.4 10 9.6 10.8 8 10.8C6.4 10.8 4.8 10.4 4 9.6L2.4 12.4C3.6 13.2 5.6 14 8 14C11.6 14 14 12.4 14 9.2C14 4.8 8 6 8 4.8Z" fill="#6772E5"/>
                <path d="M28 4.4H24.4V7.2H27.6V10H24.4V14H20.8V2.8H28V4.4Z" fill="#6772E5"/>
                <path d="M36.8 2.8H33.2V14H36.8V2.8Z" fill="#6772E5"/>
                <path d="M44 2.8H40.4V14H44V2.8Z" fill="#6772E5"/>
                <rect x="0" y="13" width="48" height="3" rx="1.5" fill="#F5F5F5" opacity="0.5"/>
              </svg>
            </div>
          </div>
          <div class="payment-methods-text">
            <span>Visa</span>
            <span class="separator">•</span>
            <span>Mastercard</span>
            <span class="separator">•</span>
            <span>PayPal</span>
            <span class="separator">•</span>
            <span>Stripe</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from "@/Store";

export default {
  name: "PanierI",
  computed: {
    cart() {
      return store.cart;
    },
    totalPrice() {
      return this.cart.reduce((total, item) => total + item.price * item.quantity, 0);
    },
  },
  methods: {
    getProductImage(imagePath) {
      if (!imagePath) {
        return 'http://localhost:2028/uploads/default-product.jpg';
      }
      if (imagePath.startsWith('http')) {
        return imagePath;
      }
      if (imagePath.startsWith('/uploads/')) {
        return `http://localhost:2028${imagePath}`;
      }
      return `http://localhost:2028/uploads/${imagePath}`;
    },
    
    handleImageError(e) {
      console.log("❌ Erreur chargement image:", e.target.src);
      e.target.src = 'http://localhost:2028/uploads/default-product.jpg';
    },
    
    removeFromCart(item) {
      store.removeFromCart(item.name || item.id);
      this.saveCart();
    },
    
    increaseQuantity(item) {
      item.quantity += 1;
      this.saveCart();
    },
    
    decreaseQuantity(item) {
      if (item.quantity > 1) {
        item.quantity -= 1;
        this.saveCart();
      }
    },
    
    saveCart() {
      sessionStorage.setItem('cart', JSON.stringify(this.cart));
    },
    
    continueShopping() {
      this.$router.push("/shop");
    },
    
    checkout() {
      sessionStorage.setItem('cart', JSON.stringify(this.cart));
      const isAuthenticated = sessionStorage.getItem('isAuthenticated') === 'true';
      if (isAuthenticated) {
        this.$router.push("/shipping");
      } else {
        sessionStorage.setItem('redirectAfterLogin', '/shipping');
        this.$router.push("/connexion");
      }
    }
  },
};
</script>

<style scoped>


/* --- Reset & Base --- */
.cart-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #f7f9fc;
  min-height: 80vh;
}

/* --- En-tête --- */
.cart-header {
  margin-bottom: 40px;
}

.cart-title {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 2.2rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px 0;
}

.title-icon {
  font-size: 2.4rem;
}

.cart-count {
  font-size: 0.9rem;
  font-weight: 500;
  color: #6c7a89;
  background: #eef2f6;
  padding: 4px 14px;
  border-radius: 20px;
  margin-left: 8px;
}

.cart-subtitle {
  font-size: 1rem;
  color: #6c7a89;
  margin: 0;
}

/* --- Panier vide --- */
.empty-cart {
  text-align: center;
  padding: 80px 20px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.empty-cart-icon {
  font-size: 4rem;
  margin-bottom: 20px;
  display: block;
}

.empty-cart h2 {
  font-size: 1.8rem;
  color: #1a1a2e;
  margin-bottom: 12px;
}

.empty-cart p {
  color: #6c7a89;
  max-width: 400px;
  margin: 0 auto 30px;
  font-size: 1.05rem;
  line-height: 1.6;
}

/* --- Conteneur principal --- */
.cart-container {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 32px;
  align-items: start;
}

@media (max-width: 968px) {
  .cart-container {
    grid-template-columns: 1fr;
  }
}

/* --- Liste des articles --- */
.cart-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cart-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid #edf2f7;
  transition: all 0.3s ease;
}

.cart-item:hover {
  border-color: #cbd5e0;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}

/* --- Image --- */
.item-image-wrapper {
  position: relative;
  flex-shrink: 0;
}

.item-image {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 12px;
  background: #f7f9fc;
  border: 1px solid #edf2f7;
}

.item-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #2a9d8f;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(42, 157, 143, 0.3);
}

/* --- Détails --- */
.item-details {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 6px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-price {
  font-size: 1rem;
  font-weight: 600;
  color: #2a9d8f;
  margin: 0 0 12px 0;
}

.item-price .currency {
  font-weight: 400;
  color: #6c7a89;
  font-size: 0.8rem;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* --- Contrôles de quantité --- */
.quantity-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f4f8;
  border-radius: 10px;
  padding: 3px;
}

.qty-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #1a1a2e;
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.qty-btn:hover:not(:disabled) {
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.qty-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.qty-icon {
  line-height: 1;
}

.item-quantity {
  font-weight: 600;
  color: #1a1a2e;
  min-width: 28px;
  text-align: center;
  font-size: 0.95rem;
}

/* --- Bouton supprimer --- */
.btn-remove {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-remove:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* --- Total par article --- */
.item-total {
  text-align: right;
  flex-shrink: 0;
  min-width: 120px;
}

.total-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.total-amount {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a2e;
}

/* --- Résumé de la commande --- */
.cart-summary {
  background: #ffffff;
  border-radius: 20px;
  padding: 28px 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid #edf2f7;
  position: sticky;
  top: 20px;
}

.summary-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 24px 0;
  padding-bottom: 16px;
  border-bottom: 2px solid #f1f4f8;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 0.95rem;
  color: #475569;
}

.summary-row span:last-child {
  font-weight: 500;
  color: #1a1a2e;
}

.summary-divider {
  height: 1px;
  background: #edf2f7;
  margin: 12px 0;
}

.summary-row.total {
  padding: 12px 0 4px;
}

.summary-row.total span:first-child {
  font-weight: 700;
  font-size: 1.1rem;
  color: #1a1a2e;
}

.summary-row.total span:last-child {
  font-weight: 800;
  font-size: 1.4rem;
  color: #2a9d8f;
}

/* --- Boutons --- */
.summary-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn-primary {
  background: #2a9d8f;
  color: white;
}

.btn-primary:hover {
  background: #23877b;
  box-shadow: 0 4px 16px rgba(42, 157, 143, 0.3);
  transform: translateY(-2px);
}

.btn-secondary {
  background: #f1f4f8;
  color: #1a1a2e;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-lg {
  padding: 14px 36px;
  font-size: 1.05rem;
  border-radius: 16px;
}

.btn-checkout {
  padding: 14px 28px;
  font-size: 1.05rem;
}

.btn-checkout svg {
  flex-shrink: 0;
}

/* --- Paiement sécurisé avec logos --- */
.secure-payment {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #edf2f7;
  text-align: center;
}

.secure-icons {
  display: flex;
  justify-content: center;
  gap: 12px;
  font-size: 1.4rem;
  margin-bottom: 8px;
}

.secure-payment p {
  font-size: 0.85rem;
  color: #6c7a89;
  margin: 0 0 16px 0;
}

/* Conteneur des logos */
.payment-methods {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

/* Style des logos de paiement */
.payment-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #edf2f7;
  transition: all 0.3s ease;
  cursor: default;
  height: 32px;
}

.payment-logo:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.payment-logo svg {
  height: 20px;
  width: auto;
}

.payment-logo.visa svg path {
  fill: #1434CB;
}

.payment-logo.mastercard svg circle:first-child {
  fill: #EB001B;
}

.payment-logo.mastercard svg circle:last-child {
  fill: #F79E1B;
}

.payment-logo.paypal svg path {
  fill: #003087;
}

.payment-logo.stripe svg path {
  fill: #6772E5;
}

/* Texte des méthodes de paiement */
.payment-methods-text {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: #94a3b8;
  flex-wrap: wrap;
}

.payment-methods-text .separator {
  color: #cbd5e0;
}

.payment-methods-text span:not(.separator) {
  font-weight: 500;
  letter-spacing: 0.3px;
}

/* --- Responsive --- */
@media (max-width: 768px) {
  .cart-page {
    padding: 20px 16px;
  }

  .cart-title {
    font-size: 1.6rem;
    flex-wrap: wrap;
  }

  .cart-item {
    flex-wrap: wrap;
    padding: 16px;
  }

  .item-image-wrapper {
    width: 80px;
    height: 80px;
  }

  .item-image {
    width: 80px;
    height: 80px;
  }

  .item-name {
    font-size: 0.95rem;
    white-space: normal;
  }

  .item-total {
    min-width: auto;
    text-align: left;
    width: 100%;
    padding-left: 96px;
    margin-top: 8px;
  }

  .cart-summary {
    padding: 20px;
  }

  .summary-row.total span:last-child {
    font-size: 1.2rem;
  }

  .payment-methods {
    gap: 10px;
  }

  .payment-logo {
    padding: 4px 10px;
    height: 28px;
  }

  .payment-logo svg {
    height: 16px;
  }
}

@media (max-width: 480px) {
  .cart-title {
    font-size: 1.4rem;
  }

  .cart-count {
    font-size: 0.7rem;
    padding: 2px 10px;
  }

  .item-actions {
    flex-wrap: wrap;
  }

  .btn-checkout {
    font-size: 0.95rem;
    padding: 12px 20px;
  }

  .payment-methods-text {
    font-size: 0.65rem;
    gap: 4px;
  }
}

/* --- Mode sombre --- */
@media (prefers-color-scheme: dark) {
  .cart-page {
    background: #0f1720;
  }

  .cart-title {
    color: #e6edf1;
  }

  .cart-subtitle {
    color: #8b97a3;
  }

  .cart-count {
    background: #1a2532;
    color: #8b97a3;
  }

  .empty-cart {
    background: #1a2532;
  }

  .empty-cart h2 {
    color: #e6edf1;
  }

  .empty-cart p {
    color: #8b97a3;
  }

  .cart-item {
    background: #1a2532;
    border-color: #2a3645;
  }

  .cart-item:hover {
    border-color: #3a4a5b;
  }

  .item-image {
    background: #0f1720;
    border-color: #2a3645;
  }

  .item-name {
    color: #e6edf1;
  }

  .item-price {
    color: #4ade80;
  }

  .quantity-controls {
    background: #0f1720;
  }

  .qty-btn:hover:not(:disabled) {
    background: #1a2532;
  }

  .item-quantity {
    color: #e6edf1;
  }

  .btn-remove:hover {
    background: #331a1a;
  }

  .total-amount {
    color: #e6edf1;
  }

  .cart-summary {
    background: #1a2532;
    border-color: #2a3645;
  }

  .summary-title {
    color: #e6edf1;
    border-bottom-color: #2a3645;
  }

  .summary-row {
    color: #8b97a3;
  }

  .summary-row span:last-child {
    color: #e6edf1;
  }

  .summary-divider {
    background: #2a3645;
  }

  .summary-row.total span:first-child {
    color: #e6edf1;
  }

  .summary-row.total span:last-child {
    color: #4ade80;
  }

  .btn-secondary {
    background: #0f1720;
    color: #e6edf1;
  }

  .btn-secondary:hover {
    background: #1a2532;
  }

  .secure-payment {
    border-top-color: #2a3645;
  }

  .secure-payment p {
    color: #8b97a3;
  }

  .payment-logo {
    background: #1a2532;
    border-color: #2a3645;
  }

  .payment-logo:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .payment-methods-text {
    color: #6c7a89;
  }

  .payment-methods-text .separator {
    color: #2a3645;
  }
}
</style>